import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';

const SESSION_KEY = 'pubsilo_buy_params';

export default function BuyPage() {
  const navigate = useNavigate();

  // ── URL params ───────────────────────────────────────────────────────────────
  const params    = new URLSearchParams(window.location.search);
  const key       = params.get('key');
  const article   = params.get('article');   // publisher's own slug
  const titleHint = params.get('title') || article;
  const priceHint = params.get('price');
  const returnTo  = params.get('return_to');
  const audience  = params.get('audience');

  // Persist across login redirect
  useEffect(() => {
    if (key && article) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ key, article, title: titleHint, price: priceHint, return_to: returnTo, audience }));
    }
  }, [key, article, titleHint, priceHint, returnTo, audience]);

  // Recover if we came back from login with no params
  const saved      = (() => { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; } })();
  const effectiveKey       = key       || saved?.key;
  const effectiveSlug      = article   || saved?.article;
  const effectiveTitleHint = titleHint || saved?.title;
  const effectivePriceHint = priceHint || saved?.price;
  const effectiveRt        = returnTo  || saved?.return_to;
  const effectiveAudience  = audience  || saved?.audience || 'local';

  // ── Auth ─────────────────────────────────────────────────────────────────────
  const { accessToken: token } = useAuth();
  const userType   = localStorage.getItem('userType');
  const isLoggedIn = !!token && userType === 'user';

  // ── State ────────────────────────────────────────────────────────────────────
  const [articleData, setArticleData] = useState(null);
  const [user, setUser]               = useState(null);
  const [phase, setPhase]             = useState('loading');
  const [msg, setMsg]                 = useState('');

  // ── Load article + user ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!effectiveKey || !effectiveSlug) {
      setPhase('error');
      setMsg('Missing embed key or article slug.');
      return;
    }
    if (!isLoggedIn) {
      setPhase('auth');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get(`/api/embed/article?key=${encodeURIComponent(effectiveKey)}&article=${encodeURIComponent(effectiveSlug)}&title=${encodeURIComponent(effectiveTitleHint || '')}&price=${encodeURIComponent(effectivePriceHint || 1)}${effectiveRt ? `&return_to=${encodeURIComponent(effectiveRt)}` : ''}&audience=${encodeURIComponent(effectiveAudience)}`),
      axios.get('/api/users/me', { headers }),
    ])
      .then(([artRes, userRes]) => {
        const art = artRes.data;
        const usr = userRes.data;
        console.log('BuyPage loaded:', { art, tokens: usr.tokens, purchased: usr.purchased?.length });
        setArticleData(art);
        setUser(usr);

        // Already purchased → get token and redirect immediately
        const alreadyOwned = (usr.purchased || []).some(
          p => String(p.articleId) === String(art._id)
        );
        if (alreadyOwned) {
          fetchTokenAndRedirect();
          return;
        }

        const balance   = usr.tokens || 0;
        const cost      = art.token_cost;
        const shortfall = cost - balance;
        const needsPromo = balance > 0 && shortfall > 0 && shortfall <= 15 && (usr.promoTokens || 0) === 0;

        if (balance >= cost || needsPromo) {
          setPhase(needsPromo ? 'promo' : 'confirm');
        } else {
          setPhase('buying');
        }
      })
      .catch(err => {
        console.error('BuyPage load error:', err.response?.status, err.response?.data, err.message);
        setPhase('error');
        setMsg(err.response?.data?.error || err.message || 'Failed to load article info.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  async function fetchTokenAndRedirect() {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `/api/embed/token?key=${encodeURIComponent(effectiveKey)}&article=${encodeURIComponent(effectiveSlug)}`,
        { headers }
      );
      redirect(res.data.token);
    } catch (err) {
      console.error('fetchTokenAndRedirect error:', err.response?.status, err.response?.data, err.message);
      setPhase('error');
      setMsg(err.response?.data?.error || 'Could not generate access token. Please contact support.');
    }
  }

  function redirect(embedToken) {
    sessionStorage.removeItem(SESSION_KEY);
    const sep = effectiveRt.includes('?') ? '&' : '?';
    window.location.href = `${effectiveRt}${sep}article=${encodeURIComponent(effectiveSlug)}&token=${encodeURIComponent(embedToken)}`;
  }

  async function handlePurchase() {
    // Open dashboard in a new tab immediately — must happen before any await
    // to stay within the browser's user-gesture window (avoids popup blockers).
    window.open('/dashboard', '_blank', 'noopener');
    setPhase('loading');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post('/api/embed/purchase', {
        key:       effectiveKey,
        article:   effectiveSlug,
        title:     effectiveTitleHint,
        price:     effectivePriceHint,
        return_to: effectiveRt,
        audience:  effectiveAudience,
      }, { headers });
      redirect(res.data.token);
    } catch (err) {
      setPhase('error');
      setMsg(err.response?.data?.error || 'Purchase failed. Please try again.');
    }
  }

  // ── Login / signup links ──────────────────────────────────────────────────────
  const buyParams = new URLSearchParams({ key: effectiveKey || '', article: effectiveSlug || '', title: effectiveTitleHint || '', price: effectivePriceHint || '', return_to: effectiveRt || '' });
  const loginHref  = `/login?next=${encodeURIComponent(`/buy?${buyParams}`)}`;
  const signupHref = `/signup/reader?next=${encodeURIComponent(`/buy?${buyParams}`)}`;
  const tokenPurchaseHref = `/tokens/purchase?next=${encodeURIComponent(`/buy?${buyParams}`)}`;

  // ── UI ────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', backgroundColor: 'var(--pubsilo-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '2rem 1rem',
    }}>
      <div style={{
        background: '#fff', borderRadius: '14px', padding: '2.5rem 2rem',
        maxWidth: '440px', width: '100%',
        boxShadow: '0 12px 40px rgba(13,49,101,0.15)',
      }}>
        <img src="/pubsilo.png" alt="PubSilo" style={{ height: 56, objectFit: 'contain', display: 'block', margin: '0 auto 1.5rem' }} />

        {/* LOADING */}
        {phase === 'loading' && (
          <p style={{ textAlign: 'center', color: 'var(--pubsilo-muted)' }}>Loading…</p>
        )}

        {/* ERROR */}
        {phase === 'error' && (
          <>
            <h2 style={hd}>Something went wrong</h2>
            <p style={sub}>{msg}</p>
            {effectiveRt && <a href={effectiveRt} style={btnPrimary}>← Return to article</a>}
          </>
        )}

        {/* NOT LOGGED IN */}
        {phase === 'auth' && (
          <>
            <h2 style={hd}>Access this article on PubSilo</h2>
            <p style={sub}>Sign in or create a free account to purchase and unlock content.</p>
            {effectiveTitleHint && (
              <p style={{ ...sub, fontStyle: 'italic', marginBottom: '1.25rem' }}>
                "{effectiveTitleHint}"
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
              <a href={loginHref} style={btnPrimary}>Sign In</a>
              <a href={signupHref} style={btnSecondary}>Create Account</a>
            </div>
            {effectiveRt && (
              <a href={effectiveRt} style={backLink}>← Return without purchasing</a>
            )}
          </>
        )}

        {/* CONFIRM / PARTIAL */}
        {(phase === 'confirm' || phase === 'promo') && articleData && user && (
          <>
            <div style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ ...hd, marginBottom: '0.35rem' }}>{articleData.title}</h2>
              {articleData.company_name && (
                <p style={{ ...sub, marginBottom: 0 }}>Published by <strong>{articleData.company_name}</strong></p>
              )}
            </div>

            <div style={{
              background: 'var(--pubsilo-light)', borderRadius: '9px',
              padding: '1rem 1.25rem', marginBottom: '1.5rem',
              border: '1px solid var(--pubsilo-border)',
            }}>
              <Row label="Article price" value={`${articleData.token_cost} credit${articleData.token_cost !== 1 ? 's' : ''}`} />
              <Row label="Your balance"  value={`${user.tokens} credit${user.tokens !== 1 ? 's' : ''}`} />
              {phase === 'confirm' && (
                <Row label="Balance after purchase" value={`${user.tokens - articleData.token_cost} credits`} highlight />
              )}
              {phase === 'promo' && (
                <Row label="Promo credits gifted" value={`${articleData.token_cost - user.tokens} credit${(articleData.token_cost - user.tokens) !== 1 ? 's' : ''}`} highlight />
              )}
              {phase === 'promo' && (
                <Row label="Balance after purchase" value="0 credits" highlight warn />
              )}
            </div>

            {phase === 'promo' && (
              <p style={{ fontSize: '0.85rem', color: '#1a5e9a', marginBottom: '1rem', background: '#eef4ff', borderRadius: '7px', padding: '0.75rem 1rem', border: '1px solid #b3d0f5' }}>
                You're {articleData.token_cost - user.tokens} credit{articleData.token_cost - user.tokens !== 1 ? 's' : ''} short. We'll gift you the difference so you can purchase now.
              </p>
            )}

            <button onClick={() => handlePurchase()} style={btnPrimaryBtn}>
              {phase === 'promo'
                ? `Purchase · ${articleData.token_cost} credits (${articleData.token_cost - user.tokens} gifted)`
                : `Purchase · ${articleData.token_cost} credits`}
            </button>

            {phase === 'promo' && (
              <button onClick={() => navigate(tokenPurchaseHref)} style={{ ...btnSecondaryBtn, marginTop: '0.5rem' }}>
                Buy More Article Credits Instead
              </button>
            )}

            {effectiveRt && (
              <a href={effectiveRt} style={backLink}>← Return without purchasing</a>
            )}
          </>
        )}

        {/* NOT ENOUGH ARTICLE CREDITS */}
        {phase === 'buying' && articleData && user && (
          <>
            <h2 style={hd}>Not enough Article Credits</h2>
            <div style={{
              background: 'var(--pubsilo-light)', borderRadius: '9px',
              padding: '1rem 1.25rem', marginBottom: '1.5rem',
              border: '1px solid var(--pubsilo-border)',
            }}>
              <Row label="Article price" value={`${articleData.token_cost} credits`} />
              <Row label="Your balance"  value={`${user.tokens} credits`} />
              <Row label="You need"      value={`${articleData.token_cost - user.tokens} more credits`} highlight warn />
            </div>
            <p style={{ fontSize: '0.88rem', color: 'var(--pubsilo-muted)', marginBottom: '1.25rem' }}>
              Purchase Article Credits to unlock <strong>{articleData.title}</strong>.
            </p>
            <button onClick={() => navigate(tokenPurchaseHref)} style={btnPrimaryBtn}>
              Buy Article Credits
            </button>
            {effectiveRt && (
              <a href={effectiveRt} style={backLink}>← Return without purchasing</a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────
function Row({ label, value, highlight, warn }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', padding: '0.2rem 0' }}>
      <span style={{ color: 'var(--pubsilo-muted)' }}>{label}</span>
      <span style={{ fontWeight: 700, color: warn ? '#b06000' : highlight ? 'var(--pubsilo-blue)' : 'var(--pubsilo-text)' }}>{value}</span>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const hd = { fontSize: '1.3rem', fontWeight: 800, color: 'var(--pubsilo-blue-dark)', marginBottom: '0.5rem', textAlign: 'center' };
const sub = { fontSize: '0.9rem', color: 'var(--pubsilo-muted)', textAlign: 'center', marginBottom: '0.5rem' };
const btnPrimary = {
  display: 'block', width: '100%', padding: '0.75rem', textAlign: 'center',
  backgroundColor: 'var(--pubsilo-blue)', color: '#fff', borderRadius: '7px',
  fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem',
};
const btnSecondary = {
  display: 'block', width: '100%', padding: '0.75rem', textAlign: 'center',
  backgroundColor: 'transparent', color: 'var(--pubsilo-blue)', borderRadius: '7px',
  fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem',
  border: '1.5px solid var(--pubsilo-blue)',
};
const btnPrimaryBtn  = { ...btnPrimary,    border: 'none', cursor: 'pointer', display: 'block', width: '100%' };
const btnSecondaryBtn = { ...btnSecondary, cursor: 'pointer', background: 'transparent' };
const backLink = {
  display: 'block', textAlign: 'center', marginTop: '1.25rem',
  fontSize: '0.82rem', color: 'var(--pubsilo-muted)', textDecoration: 'none',
};
